// Service de synchronisation
import api from './api';
import database from './database';

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
  }

  async start() {
    // Démarrer la synchronisation automatique
    const interval = parseInt(process.env.REACT_APP_SYNC_INTERVAL) || 30000;
    
    // Sync immédiate
    await this.sync();
    
    // Sync périodique
    this.syncInterval = setInterval(() => {
      this.sync();
    }, interval);
  }

  stop() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async sync() {
    if (this.isSyncing) return;
    
    // Vérifier si on est en ligne avant de synchroniser
    if (!this.isOnline()) {
      console.log('Mode offline: synchronisation reportée');
      return;
    }
    
    try {
      this.isSyncing = true;
      
      // 1. Récupérer les changements locaux non synchronisés
      const pendingChanges = await database.getPendingSync();
      
      // 2. Envoyer les changements au serveur (seulement s'il y en a et si on est en ligne)
      if (pendingChanges.length > 0 && this.isOnline()) {
        try {
          // Préparer les changements avec métadonnées pour la résolution de conflits
          const changesWithMetadata = pendingChanges.map(change => ({
            ...change,
            local_updated_at: change.updated_at || change.created_at,
            local_synced: change.synced || 0
          }));

          // Générer ou récupérer un device_id unique
          let deviceId = localStorage.getItem('device_id');
          if (!deviceId) {
            deviceId = `desktop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('device_id', deviceId);
          }

          const response = await api.post('/sync/push', { 
            device_id: deviceId,
            device_type: 'desktop',
            changes: changesWithMetadata 
          });
          
          // Vérifier s'il y a des conflits signalés par le serveur
          if (response.data && response.data.conflicts) {
            console.warn(`${response.data.conflicts.length} conflit(s) détecté(s) par le serveur`);
            // Traiter les conflits signalés
            await this.handleServerConflicts(response.data.conflicts);
          }
          
          // Marquer comme synchronisé seulement si pas de conflit
          const syncedIds = pendingChanges
            .filter(c => !response.data?.conflicts?.some(conflict => conflict.id === c.id))
            .map(c => c.id);
          
          if (syncedIds.length > 0) {
            await database.markAsSynced(syncedIds);
          }
          
          console.log(`${syncedIds.length} changement(s) synchronisé(s) avec succès`);
        } catch (pushError) {
          // Si on perd la connexion pendant l'envoi, on garde les changements pour plus tard
          if (!this.isOnline()) {
            console.log('Connexion perdue pendant la synchronisation');
            return;
          }
          // Ignorer les erreurs 204 (pas de contenu) ou 404
          if (pushError.response?.status !== 204 && pushError.response?.status !== 404) {
            throw pushError;
          }
        }
      }
      
      // 3. Récupérer les changements du serveur (seulement si on est en ligne)
      if (this.isOnline()) {
        // Générer ou récupérer un device_id unique
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) {
          deviceId = `desktop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('device_id', deviceId);
        }
        
        const lastSyncTime = localStorage.getItem('lastSyncTime') || new Date(0).toISOString();
        try {
          const response = await api.post('/sync/pull', { 
            device_id: deviceId,
            device_type: 'desktop',
            last_sync_hash: localStorage.getItem('lastSyncHash') || null,
            entity_types: ['transactions', 'positions', 'stock']
          });
          
          // 4. Appliquer les changements locaux (seulement s'il y en a)
          if (response.data && response.data.changes && response.data.changes.length > 0) {
            for (const change of response.data.changes) {
              await this.applyServerChange(change);
            }
            console.log(`${response.data.changes.length} changement(s) reçu(s) du serveur`);
          }
          
          // 5. Mettre à jour le timestamp de dernière sync
          localStorage.setItem('lastSyncTime', new Date().toISOString());
        } catch (pullError) {
          // Si on perd la connexion, on arrête
          if (!this.isOnline()) {
            console.log('Connexion perdue pendant la récupération');
            return;
          }
          // Ignorer les erreurs 204 (pas de changements) ou 404
          if (pullError.response?.status !== 204 && pullError.response?.status !== 404) {
            throw pullError;
          }
        }
      }
      
    } catch (error) {
      // Ne logger que les vraies erreurs (pas les 204, 404, ou 400 pour "pas de changements")
      if (error.response?.status !== 204 && 
          error.response?.status !== 404 && 
          error.response?.status !== 400) {
        console.error('Erreur de synchronisation:', error);
      }
      // En mode offline, 204, 404, ou 400 (pas de changements), on continue sans erreur
      // Ne pas throw l'erreur si c'est juste qu'il n'y a rien à synchroniser
      if (error.response?.status === 400 && error.response?.data?.message?.includes('No changes')) {
        // Pas de changements à synchroniser, c'est normal
        return;
      }
      if (error.response?.status !== 204 && error.response?.status !== 404) {
        throw error;
      }
    } finally {
      this.isSyncing = false;
    }
  }

  async applyServerChange(change) {
    // Appliquer un changement reçu du serveur avec résolution de conflits
    try {
      switch (change.type) {
        case 'create':
          await this.handleCreate(change);
          break;
        case 'update':
          await this.handleUpdate(change);
          break;
        case 'delete':
          await this.handleDelete(change);
          break;
        default:
          console.warn('Type de changement inconnu:', change.type);
      }
    } catch (error) {
      console.error('Erreur lors de l\'application du changement:', error);
      // Ne pas bloquer la synchronisation pour un seul changement
      throw error;
    }
  }

  async handleCreate(change) {
    // Vérifier si la transaction existe déjà localement
    try {
      const existing = await database.getTransactionById(change.id);
      
      if (existing) {
        // Conflit : transaction existe déjà localement
        console.warn(`Conflit CREATE: Transaction ${change.id} existe déjà localement`);
        
        // Vérifier si la transaction locale a été modifiée depuis la dernière sync
        const localUpdated = new Date(existing.updated_at || existing.created_at);
        const serverUpdated = new Date(change.data.updated_at || change.data.created_at);
        
        if (localUpdated > serverUpdated) {
          // La version locale est plus récente, on la garde
          console.log(`Version locale plus récente pour ${change.id}, conservation de la version locale`);
          // Marquer comme synchronisée pour éviter les conflits futurs
          await database.markAsSynced([change.id]);
          return;
        } else {
          // La version serveur est plus récente ou égale, on l'applique
          console.log(`Version serveur plus récente pour ${change.id}, application de la version serveur`);
          await database.updateTransaction(change.id, {
            ...change.data,
            synced: 1
          });
        }
      } else {
        // Pas de conflit, créer la transaction
        await database.createTransaction({
          ...change.data,
          id: change.id,
          synced: 1
        });
      }
    } catch (error) {
      // Si la transaction existe déjà (UNIQUE constraint), c'est un conflit
      if (error.message && error.message.includes('UNIQUE')) {
        console.warn(`Conflit CREATE: Transaction ${change.id} existe déjà (contrainte unique)`);
        // Essayer de mettre à jour au lieu de créer
        await this.handleUpdate(change);
      } else {
        throw error;
      }
    }
  }

  async handleUpdate(change) {
    // Récupérer la version locale actuelle
    const localTransaction = await database.getTransactionById(change.id);
    
    if (!localTransaction) {
      // Transaction n'existe pas localement, la créer
      console.log(`Transaction ${change.id} n'existe pas localement, création depuis serveur`);
      await this.handleCreate(change);
      return;
    }

    // Vérifier si la transaction locale a été modifiée depuis la dernière sync
    const isLocalModified = localTransaction.synced === 0;
    const localUpdated = new Date(localTransaction.updated_at || localTransaction.created_at);
    const serverUpdated = new Date(change.data.updated_at || change.data.created_at);
    
    // Vérifier si la transaction est verrouillée localement
    const isLocked = localTransaction.is_locked === 1;
    
    if (isLocked) {
      // Transaction verrouillée : ne pas écraser
      console.log(`Transaction ${change.id} est verrouillée localement, conservation de la version locale`);
      return;
    }

    if (isLocalModified && localUpdated > serverUpdated) {
      // Conflit : version locale modifiée et plus récente
      console.warn(`Conflit UPDATE: Transaction ${change.id} modifiée localement et plus récente`);
      
      // Stratégie : Last Write Wins avec vérification intelligente
      // Si la différence est minime (< 1 seconde), on privilégie le serveur (source de vérité)
      const timeDiff = Math.abs(localUpdated - serverUpdated);
      
      if (timeDiff < 1000) {
        // Différence < 1 seconde : privilégier le serveur
        console.log(`Différence temporelle minime, application de la version serveur pour ${change.id}`);
        await database.updateTransaction(change.id, {
          ...change.data,
          synced: 1
        });
      } else {
        // Différence significative : garder la version locale et marquer comme non synchronisée
        // Le serveur recevra la version locale lors de la prochaine sync push
        console.log(`Conservation de la version locale pour ${change.id}, sera envoyée au serveur`);
        // Ne pas marquer comme synchronisée pour qu'elle soit renvoyée au serveur
      }
    } else if (serverUpdated >= localUpdated) {
      // Version serveur plus récente ou égale, l'appliquer
      console.log(`Application de la version serveur pour ${change.id}`);
      await database.updateTransaction(change.id, {
        ...change.data,
        synced: 1
      });
    } else {
      // Version locale plus récente mais non modifiée localement
      // Cela ne devrait pas arriver, mais on garde la version locale
      console.log(`Version locale plus récente pour ${change.id}, conservation`);
    }
  }

  async handleDelete(change) {
    // Vérifier si la transaction existe localement
    const localTransaction = await database.getTransactionById(change.id);
    
    if (!localTransaction) {
      // Déjà supprimée localement, rien à faire
      console.log(`Transaction ${change.id} déjà supprimée localement`);
      return;
    }

    // Vérifier si la transaction a été modifiée localement depuis la dernière sync
    const isLocalModified = localTransaction.synced === 0;
    const localUpdated = new Date(localTransaction.updated_at || localTransaction.created_at);
    const serverDeleted = new Date(change.deleted_at || change.timestamp);
    
    // Vérifier si la transaction est verrouillée
    const isLocked = localTransaction.is_locked === 1;
    
    if (isLocked) {
      // Transaction verrouillée : ne pas supprimer
      console.log(`Transaction ${change.id} est verrouillée, conservation malgré la suppression serveur`);
      return;
    }

    if (isLocalModified && localUpdated > serverDeleted) {
      // Conflit : transaction modifiée localement après la suppression serveur
      console.warn(`Conflit DELETE: Transaction ${change.id} modifiée localement après suppression serveur`);
      
      // Stratégie : si la modification locale est récente, on la garde
      // Sinon, on applique la suppression serveur
      const timeDiff = Math.abs(localUpdated - serverDeleted);
      
      if (timeDiff < 5000) {
        // Modification locale très récente (< 5 secondes), on garde la transaction
        console.log(`Modification locale récente pour ${change.id}, conservation de la transaction`);
        // Marquer comme synchronisée pour éviter les conflits futurs
        await database.markAsSynced([change.id]);
      } else {
        // Suppression serveur plus ancienne, on l'applique
        console.log(`Application de la suppression serveur pour ${change.id}`);
        await database.deleteTransaction(change.id);
      }
    } else {
      // Pas de conflit, appliquer la suppression
      console.log(`Suppression de la transaction ${change.id} depuis le serveur`);
      await database.deleteTransaction(change.id);
    }
  }

  async handleServerConflicts(conflicts) {
    // Traiter les conflits signalés par le serveur
    for (const conflict of conflicts) {
      console.warn(`Conflit détecté pour transaction ${conflict.id}:`, conflict.reason);
      
      // Récupérer la version locale
      const localTransaction = await database.getTransactionById(conflict.id);
      
      if (!localTransaction) {
        continue;
      }

      // Stratégie de résolution selon le type de conflit
      switch (conflict.type) {
        case 'concurrent_update':
          // Mise à jour concurrente : comparer les timestamps
          const localTime = new Date(localTransaction.updated_at || localTransaction.created_at);
          const serverTime = new Date(conflict.server_updated_at);
          
          if (localTime > serverTime) {
            // Version locale plus récente, on la garde et on la renverra
            console.log(`Conservation de la version locale pour ${conflict.id}`);
          } else {
            // Version serveur plus récente, on l'applique
            console.log(`Application de la version serveur pour ${conflict.id}`);
            await database.updateTransaction(conflict.id, {
              ...conflict.server_data,
              synced: 1
            });
          }
          break;
          
        case 'deleted_on_server':
          // Supprimée sur le serveur mais modifiée localement
          if (localTransaction.is_locked) {
            // Transaction verrouillée, on la garde
            console.log(`Transaction ${conflict.id} verrouillée, conservation`);
          } else {
            // Appliquer la suppression
            console.log(`Application de la suppression serveur pour ${conflict.id}`);
            await database.deleteTransaction(conflict.id);
          }
          break;
          
        default:
          console.warn(`Type de conflit inconnu: ${conflict.type}`);
      }
    }
  }

  isOnline() {
    return navigator.onLine;
  }
}

export default new SyncService();
